import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Separator } from "@/components/ui/separator"
import { Badge } from "@/components/ui/badge"
import { ShoppingBag, Truck, Package, Search, RefreshCcw } from "lucide-react"
import { Link } from "react-router-dom"
import { useEffect, useMemo } from "react"
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination"
import { useAppDispatch, useAppSelector } from "@/store/hooks"
import {
  getUserOrders,
  selectUserOrders,
  selectUserOrdersLoading,
  selectUserOrdersError,
  setCurrentPage,
  setItemsPerPage,
  selectOrderPagination,
} from "@/store/slices/orderSlice"
import Loader from "@/components/loader/loader"
import { formatCurrency } from "@/utils/format"

export default function OrdersPage() {
  const dispatch = useAppDispatch()
  const orders = useAppSelector(selectUserOrders)
  const loading = useAppSelector(selectUserOrdersLoading)
  const error = useAppSelector(selectUserOrdersError)
  const pagination = useAppSelector(selectOrderPagination)

  const itemsPerPage = 3 // Display 3 orders per page

  // Fetch orders on mount and when page changes
  useEffect(() => {
    dispatch(setItemsPerPage(itemsPerPage))
    dispatch(getUserOrders({ page: pagination.currentPage, limit: itemsPerPage }))
  }, [dispatch, pagination.currentPage])

  // Transform backend data to frontend format
  const transformedOrders = useMemo(() => {
    return orders.map((order) => {
      const items = order.items || []
      const firstShipment = order.shipments?.[0] || null
      
      return {
        id: order.order_id || order.id,
        date: new Date(order.created_at || order.createdAt).toLocaleDateString("en-US", {
          month: "long",
          day: "numeric",
          year: "numeric",
        }),
        total: parseFloat(order.total || 0),
        status: order.status,
        items: items.length,
        products: items.slice(0, 3).map((item) => ({
          name: item.product_name || `Product ${item.product_id}`,
          image: item.product_image || "/placeholder.svg?height=40&width=40",
        })),
        trackingNumber: firstShipment?.tracking_number || null,
      }
    })
  }, [orders])

  // Use server-side pagination - orders are already paginated from backend
  const currentOrders = transformedOrders
  // Use server pagination if available, otherwise calculate from orders length
  const totalPages = pagination.totalPages || Math.ceil(transformedOrders.length / itemsPerPage)

  const handlePageChange = (pageNumber) => {
    dispatch(setCurrentPage(pageNumber))
    // Orders will be refetched via useEffect
  }

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case "Delivered":
        return "bg-green-100 text-green-700"
      case "Shipped":
        return "bg-blue-100 text-blue-700"
      case "Processing":
        return "bg-yellow-100 text-yellow-700"
      case "Cancelled":
        return "bg-red-100 text-red-700"
      default:
        return "bg-gray-100 text-gray-700"
    }
  }

  if (loading) {
    return <Loader />
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-gray-500">
        <Package className="w-16 h-16 mb-4" />
        <p className="text-lg">{error}</p>
        <Button onClick={() => window.location.reload()} className="mt-4">
          Retry
        </Button>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Page Header */}
      <div className="flex items-center gap-4">
        <ShoppingBag className="w-8 h-8 text-primary" />
        <h1 className="text-3xl font-bold text-primary">My Orders</h1>
      </div>
      <p className="text-gray-600">View your order history and track your shipments.</p>

      {/* Orders List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-primary">All Orders</CardTitle>
          <CardDescription>A complete list of your purchases from GlowSkin.</CardDescription>
        </CardHeader>
        <CardContent>
          {transformedOrders.length > 0 ? (
            <div className="space-y-6">
              {currentOrders.map((order, index) => (
                <div key={order.id}>
                  <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 items-center py-4">
                    {/* Order Info */}
                    <div className="flex items-center gap-4">
                      <div className="flex -space-x-2 overflow-hidden">
                        {order.products.slice(0, 3).map((product, pIndex) => (
                          <img
                            key={pIndex}
                            src={product.image || "/placeholder.svg"}
                            alt={product.name}
                            width={40}
                            height={40}
                            className="inline-block h-10 w-10 rounded-full ring-2 ring-white object-cover"
                          />
                        ))}
                        {order.products.length > 3 && (
                          <div className="inline-block h-10 w-10 rounded-full ring-2 ring-white bg-gray-200 flex items-center justify-center text-xs text-gray-600">
                            +{order.products.length - 3}
                          </div>
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-primary">Order {order.id}</p>
                        <p className="text-sm text-gray-600">{order.date}</p>
                      </div>
                    </div>

                    {/* Status & Total */}
                    <div className="flex flex-col items-start md:items-center">
                      <Badge className={getStatusBadgeColor(order.status)}>{order.status}</Badge>
                      <p className="font-bold text-primary mt-1">{formatCurrency(order.total)}</p>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col sm:flex-row gap-2 md:col-span-2 lg:col-span-2 justify-end">
                      <Link to={`/dashboard/orders/${order.id}`}>
                        <Button
                          variant="outline"
                          className="w-full sm:w-auto border-secondary text-secondary hover:bg-secondary hover:text-white bg-transparent"
                        >
                          <Search className="w-4 h-4 mr-2" /> View Details
                        </Button>
                      </Link>
                      {order.trackingNumber ? (
                        <Link to={`/dashboard/track/${order.trackingNumber}`}>
                          <Button className="w-full sm:w-auto bg-primary hover:bg-primary/90 text-white">
                            <Truck className="w-4 h-4 mr-2" /> Track Shipment
                          </Button>
                        </Link>
                      ) : (
                        <Button variant="secondary" disabled className="w-full sm:w-auto">
                          <Truck className="w-4 h-4 mr-2" /> No Tracking
                        </Button>
                      )}
                      {order.status === "Delivered" && (
                        <Button variant="ghost" className="w-full sm:w-auto text-primary hover:text-secondary">
                          <RefreshCcw className="w-4 h-4 mr-2" /> Reorder
                        </Button>
                      )}
                    </div>
                  </div>
                  {index < currentOrders.length - 1 && <Separator />}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg mb-4">You haven't placed any orders yet.</p>
              <Link to="/shop">
                <Button className="bg-primary hover:bg-primary/90 text-white">Start Shopping</Button>
              </Link>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <Pagination className="mt-8">
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    to="#"
                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                    isActive={pagination.currentPage > 1}
                  />
                </PaginationItem>
                {[...Array(totalPages)].map((_, i) => (
                  <PaginationItem key={i}>
                    <PaginationLink 
                      to="#" 
                      onClick={() => handlePageChange(i + 1)} 
                      isActive={pagination.currentPage === i + 1}
                    >
                      {i + 1}
                    </PaginationLink>
                  </PaginationItem>
                ))}
                <PaginationItem>
                  <PaginationNext
                    to="#"
                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                    isActive={pagination.currentPage < totalPages}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
